import { getCssVariable } from 'utils/app.ts'
import { ScriptableScaleContext } from 'chart.js'
import { BarChartDefaults, ChartColorKeys, GetBarChartDefaults } from 'components/charts/types.ts'

export const CHART_COLORS: { [key in ChartColorKeys]: string } = {
  green: getCssVariable('--chart-green', '#23d5ae'),
  'green-active': getCssVariable('--chart-green-active', '#1FBD9A'),
  'green-hover': getCssVariable('--chart-green-hover', '#008568'),
  'green-subdued': getCssVariable('--chart-green-subdued', 'rgba(35, 213, 174, 0.4)'),
  'green-subdued-hover': getCssVariable('--chart-green-subdued-hover', 'rgba(0, 133, 104, 0.4)'),
  blue: getCssVariable('--chart-blue', '#100061'),
  lightblue: getCssVariable('--chart-lightblue', '#69baff'),
  grey: getCssVariable('--chart-grey', '#B5B49E'),
  'grey-active': getCssVariable('--chart-grey-active', '#56554D'),
  'grey-hover': getCssVariable('--chart-grey-hover', '#232322'),
  'grey-subdued': getCssVariable('--chart-grey-subdued', 'rgba(181, 180, 158, 0.5)'),
  'grey-subdued-hover': getCssVariable('--chart-grey-subdued-hover', 'rgba(35, 35, 34, 0.5)'),
  orange: getCssVariable('--accent-orange', '#ffa010'),
  purple: getCssVariable('--chart-purple', '#a564f0'),
  'purple-hover': getCssVariable('--chart-purple-hover', '#804EBA'),
  'purple-active': getCssVariable('--chart-purple-active', '#A564F0'),
  'purple-subdued': getCssVariable('--chart-purple-subdued', 'rgba(195, 143, 255, 0.4)'),
  'purple-subdued-hover': getCssVariable('--chart-purple-subdued-hover', 'rgba(128, 78, 186, 0.4)'),
  yellow: getCssVariable('--chart-yellow', '#f6df48'),
  'yellow-hover': getCssVariable('--chart-yellow-hover', '#AD8700'),
  'yellow-active': getCssVariable('--chart-yellow-active', '#F7CE00'),
  'yellow-subdued': getCssVariable('--chart-yellow-subdued', 'rgba(246, 223, 72, 0.6)'),
  'yellow-subdued-hover': getCssVariable('--chart-yellow-subdued-hover', 'rgba(173, 135, 0, 0.6)'),
  pink: getCssVariable('--chart-pink', '#ff94f2'),
  dark: getCssVariable('--chart-dark', '#43006d'),
  textSubdued: getCssVariable('--text-subdued', '#848375'),
  gridBaseline: getCssVariable('--border-high-contrast', '#100061'),
  gridLine: 'rgba(132, 131, 117, .2)', // removed getCssVariable for safari support (black color prevention for opacity colors)
  background: '#ffffff00', // transparent
  backgroundHighlight: 'rgba(132,131,117,0.1)' // removed getCssVariable for safari support (black color prevention for opacity colors)
}

/**
 * Get bar chart default values
 * @param {GetBarChartDefaults} { isMobile }
 */
export const getBarChartDefaults = ({ isMobile }: GetBarChartDefaults): BarChartDefaults => {
  return {
    responsive: true,
    animation: {
      duration: 0
    },
    categoryPercentage: 0.75,
    barPercentage: isMobile ? 0.85 : 1,
    layout: {
      padding: {
        top: 40,
        left: 0,
        right: 0,
        bottom: 0
      }
    },
    scales: {
      y: {
        type: 'linear',
        grace: '20%',
        position: 'right',
        border: { display: false },
        ticks: {
          font: {
            family: "'bolt-gamay', sans-serif",
            size: 11
          },
          crossAlign: 'far',
          color: CHART_COLORS.textSubdued,
          padding: isMobile ? 4 : 7,
          maxTicksLimit: 6
        },
        grid: {
          drawTicks: false,
          color: (context: ScriptableScaleContext) => (context.tick.value === 0 ? CHART_COLORS.gridBaseline : CHART_COLORS.gridLine)
        }
      },
      x: {
        grid: {
          lineWidth: 0
        },
        ticks: {
          color: CHART_COLORS.textSubdued,
          font: {
            family: "'bolt-gamay', sans-serif",
            size: isMobile ? 9 : 10
          },
          minRotation: 0,
          maxRotation: 0
        }
      }
    },
    interaction: {
      intersect: !isMobile,
      mode: isMobile ? 'x' : 'index'
    }
  }
}
